import React, { useState, useEffect } from 'react';
import { X, Lock, AlertTriangle } from 'lucide-react';
import { supabase } from '../supabaseClient';

const PinVerificationModal = ({ 
  showPinModal, 
  setShowPinModal, 
  onSuccess,
  onCancel,
  transactionType = 'transaction',
  user
}) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (showPinModal) {
      setPin('');
      setError('');
    }
  }, [showPinModal]);

  if (!showPinModal) return null;

  const handleVerifyPin = async () => {
    setError('');

    if (!pin || pin.length !== 4) {
      setError('Please enter your 4-digit PIN');
      return;
    }

    if (!/^\d{4}$/.test(pin)) {
      setError('PIN must contain only numbers');
      return;
    }

    // Check if user is logged in
    if (!user || !user.id) {
      setError('Please log in to continue');
      setVerifying(false);
      return;
    }

    setVerifying(true);

    try {
      // Fetch user's stored PIN from database
      const { data: userData, error: fetchError } = await supabase
        .from('users')
        .select('transaction_pin')
        .eq('id', user.id)
        .single();

      if (fetchError) {
        console.error('Fetch error:', fetchError);
        throw new Error('Failed to verify PIN. Please try again.');
      }

      // Check if PIN is set
      if (!userData || !userData.transaction_pin) {
        setError('Transaction PIN not set. Please set your PIN in Profile Settings.');
        setVerifying(false);
        return;
      }

      // Verify PIN matches (In production, this should be hashed and verified on backend)
      if (userData.transaction_pin !== pin) {
        setError('Incorrect PIN. Please try again.');
        setPin('');
        setVerifying(false);
        return;
      }

      // PIN verified successfully
      setPin('');
      setShowPinModal(false);
      if (onSuccess) onSuccess();
      
    } catch (error) {
      setError(error.message || 'Verification failed. Please try again.');
      console.error('PIN verification error:', error);
    } finally {
      setVerifying(false);
    }
  };

  const handleCancel = () => {
    setPin('');
    setError('');
    setShowPinModal(false);
    if (onCancel) onCancel();
  };

  const handlePinInput = (value) => {
    const numericValue = value.replace(/\D/g, '').slice(0, 4);
    setPin(numericValue);
    setError('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100] p-4">
      <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-lg p-6 max-w-md w-full border-2 border-yellow-500">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold flex items-center space-x-2">
            <Lock className="text-yellow-400" size={24} />
            <span>Transaction Authorization</span>
          </h3>
          <button onClick={handleCancel}>
            <X size={24} />
          </button>
        </div>

        <div className="bg-yellow-900 bg-opacity-30 border border-yellow-600 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="text-yellow-400 flex-shrink-0 mt-1" size={20} />
            <div>
              <p className="text-sm font-medium text-yellow-200">
                Security Verification Required
              </p>
              <p className="text-xs text-yellow-300 mt-1">
                Enter your 4-digit transaction PIN to authorize this {transactionType}.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-900 bg-opacity-50 border border-red-600 rounded text-red-200 text-sm flex items-center space-x-2">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-3 text-center">
              Enter Transaction PIN
            </label>
            <input
              type="password"
              value={pin}
              onChange={(e) => handlePinInput(e.target.value)}
              className="w-full bg-purple-800 text-white p-4 rounded-lg border-2 border-purple-600 focus:border-yellow-400 focus:outline-none text-center text-3xl tracking-[1em] font-bold"
              placeholder="••••"
              maxLength={4}
              autoFocus
              onKeyPress={(e) => e.key === 'Enter' && pin.length === 4 && handleVerifyPin()}
            />
            <p className="text-xs text-purple-300 text-center mt-2">
              Forgot your PIN? Update it in Profile Settings
            </p>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleVerifyPin}
              disabled={verifying || pin.length !== 4}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {verifying ? 'Verifying...' : 'Authorize'}
            </button>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-purple-700">
          <p className="text-xs text-purple-300 text-center">
            This PIN is required for all send, withdraw, top-up, and convert transactions to protect your account.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PinVerificationModal;